import { createAction } from "@reduxjs/toolkit";
import { SHIP_BUILDER_ACTION_TYPES } from "../constants/actionTypes";
import {
  ShipStats,
  Hull,
  Bridge,
  WeaponSelection,
  Armor,
  CrewComposition,
  StateroomConfig,
  ShipDetails,
} from "../../types/ship";
import { Drive, DriveType } from "../../types/drive";
import { PowerPlant } from "../../types/powerPlants";
import { HullSelection } from "../../types/hull";
import { ArmorSelection } from "../../types/armor";
import { DriveSelection } from "../../types/drive";
import { PowerPlantSelection } from "../../types/powerPlant";
import { BridgeSelection } from "../../types/bridge";
import { ComputerSelection } from "../../types/computer";
import { SoftwareSelection } from "../../types/software";
import { OptionalSystemsSelection } from "../../types/optionalSystems";
import { CrewSelection } from "../../types/crew";
import { StateroomSelection } from "../../types/stateroom";
import { FinalDetailsSelection } from "../../types/finalDetails";
import { FuelSelection } from "../../types/fuel";
import { CargoSelection } from "../../types/cargo";
import { WeaponsSelection } from "../../types/weapons";

// Action Creators
export const updateShipStats = createAction<Partial<ShipStats>>(
  SHIP_BUILDER_ACTION_TYPES.UPDATE_SHIP_STATS,
);
export const setHull = createAction<Hull>(SHIP_BUILDER_ACTION_TYPES.SET_HULL);
export const setDrives = createAction<Drive[]>(
  SHIP_BUILDER_ACTION_TYPES.SET_DRIVES,
);
export const setPowerPlant = createAction<PowerPlant>(
  SHIP_BUILDER_ACTION_TYPES.SET_POWER_PLANT,
);
export const setBridge = createAction<Bridge>(
  SHIP_BUILDER_ACTION_TYPES.SET_BRIDGE,
);
export const setWeapons = createAction<WeaponSelection[]>(
  SHIP_BUILDER_ACTION_TYPES.SET_WEAPONS,
);
export const setArmor = createAction<Armor | null>(
  SHIP_BUILDER_ACTION_TYPES.SET_ARMOR,
);
export const setSensors = createAction<string>(
  SHIP_BUILDER_ACTION_TYPES.SET_SENSORS,
);
export const setCrewComposition = createAction<CrewComposition>(
  SHIP_BUILDER_ACTION_TYPES.SET_CREW_COMPOSITION,
);
export const setStateroomConfig = createAction<StateroomConfig>(
  SHIP_BUILDER_ACTION_TYPES.SET_STATEROOM_CONFIG,
);
export const setCargoSpace = createAction<number>(
  SHIP_BUILDER_ACTION_TYPES.SET_CARGO_SPACE,
);
export const setFuelTanks = createAction<number>(
  SHIP_BUILDER_ACTION_TYPES.SET_FUEL_TANKS,
);
export const setSoftware = createAction<SoftwareSelection>(
  SHIP_BUILDER_ACTION_TYPES.SET_SOFTWARE,
);
export const setShipDetails = createAction<ShipDetails>(
  SHIP_BUILDER_ACTION_TYPES.SET_SHIP_DETAILS,
);
export const setFinalDetails = createAction<{
  name: string;
  description: string;
  notes: string;
}>(SHIP_BUILDER_ACTION_TYPES.SET_FINAL_DETAILS);
export const setCurrentStep = createAction<number>(
  SHIP_BUILDER_ACTION_TYPES.SET_CURRENT_STEP,
);

// Additional actions
export const setStateroomsAction = createAction<StateroomConfig>(
  "shipBuilder/setStaterooms",
);
export const setShipSize = createAction<number>(
  SHIP_BUILDER_ACTION_TYPES.SET_SHIP_SIZE,
);
export const setTechLevel = createAction<number>(
  SHIP_BUILDER_ACTION_TYPES.SET_TECH_LEVEL,
);

/**
 * Action creator for setting a drive with proper validation and calculation
 */
export const setDriveWithValidation = createAction<{
  type: DriveType;
  rating: number | "none";
  section?: number;
}>("shipBuilder/setDriveWithValidation");

/**
 * Action creator for removing a drive
 */
export const removeDrive = createAction<DriveType>("shipBuilder/removeDrive");

// Ship Builder Actions
export const setHullSelection = createAction<HullSelection>(
  "shipBuilder/setHullSelection",
);
export const setArmorSelection = createAction<ArmorSelection>(
  "shipBuilder/setArmorSelection",
);
export const setDriveSelection = createAction<DriveSelection>(
  "shipBuilder/setDriveSelection",
);
export const setPowerPlantSelection = createAction<PowerPlantSelection>(
  "shipBuilder/setPowerPlantSelection",
);
export const setBridgeSelection = createAction<BridgeSelection>(
  "shipBuilder/setBridgeSelection",
);
export const setComputerSelection = createAction<ComputerSelection>(
  "shipBuilder/setComputerSelection",
);
export const setSoftwareSelection = createAction<SoftwareSelection>(
  "shipBuilder/setSoftwareSelection",
);
export const setOptionalSystemsSelection =
  createAction<OptionalSystemsSelection>(
    "shipBuilder/setOptionalSystemsSelection",
  );
export const setCrewSelection = createAction<CrewSelection>(
  "shipBuilder/setCrewSelection",
);
export const setStateroomSelection = createAction<StateroomSelection>(
  "shipBuilder/setStateroomSelection",
);
export const setFinalDetailsSelection = createAction<FinalDetailsSelection>(
  "shipBuilder/setFinalDetailsSelection",
);
export const setFuelSelection = createAction<FuelSelection>(
  "shipBuilder/setFuelSelection",
);
export const setCargoSelection = createAction<CargoSelection>(
  "shipBuilder/setCargoSelection",
);
export const setWeaponsSelection = createAction<WeaponsSelection>(
  "shipBuilder/setWeaponsSelection",
);

// Software Actions
export const installSoftware = createAction<{
  packageId: string;
  level: number;
}>("shipBuilder/installSoftware");
export const uninstallSoftware = createAction<{
  packageId: string;
  level: number;
  instanceId?: string;
}>("shipBuilder/uninstallSoftware");
export const setSoftwareLevel = createAction<{
  packageId: string;
  level: number;
}>("shipBuilder/setSoftwareLevel");
export const startSoftware = createAction<{ packageId: string; level: number; instanceId?: string }>(
  "shipBuilder/startSoftware",
);
export const stopSoftware = createAction<{ packageId: string; level: number; instanceId?: string }>(
  "shipBuilder/stopSoftware",
);

// Software Configuration Actions
export const setExpertSkill = createAction<{ instanceId: string; skill: string }>(
  "shipBuilder/setExpertSkill",
);
export const setTranslatorLanguage = createAction<{ instanceId: string; language: string }>(
  "shipBuilder/setTranslatorLanguage",
);
export const setDatabaseTopic = createAction<{ instanceId: string; topic: string }>(
  "shipBuilder/setDatabaseTopic",
);
export const setVirtualCrewRoles = createAction<{ instanceId: string; roles: string[] }>(
  "shipBuilder/setVirtualCrewRoles",
);
export const setVirtualGunnerRoles = createAction<{ instanceId: string; roles: string[] }>(
  "shipBuilder/setVirtualGunnerRoles",
);
